import React, { useEffect, useRef, useState } from 'react'
import {
    Chart,
    type ChartData,
    type ChartOptions,
    registerables,
} from 'chart.js'
import styles from './Itindex.less'
import { Card, Col, Empty, Row, Select } from 'antd'
import { connect, useDispatch } from 'react-redux'

import { GetUserList } from '../../../Services/User'
import {
    type ITSTaffprogressDetailsData,
    type ITStaffProgressStateInteface,
} from '../../../Types/ITDashbord'
import { GetITStaffProgressDetails } from '../../../Services/ITDashboard'

Chart.register(...registerables)

const TaskProgressChart = (
    props: ITStaffProgressStateInteface
): React.ReactElement => {
    const { userList, ItStaffProgressList } = props
    const chartRef = useRef<HTMLCanvasElement>(null)
    const chartInstance = useRef<Chart | null>(null) // Store the chart instance
    const dispatch = useDispatch()
    const [chartHeight, setChartHeight] = useState<number>(300)
    const [legendItems] = useState<Array<{ color: string; text: string }>>([
        { color: '#7DA8FF', text: 'To Do' },
        { color: '#B9ADF9', text: 'In Progress' },
        { color: '#6EE7B7', text: 'Done' },
    ])

    // Set a fixed container height for the chart area (excluding legend)
    const chartContainerHeight = 200
    // Set a height for the legend area
    const legendHeight = 10

    useEffect(() => {
        GetUserList(dispatch)
        GetITStaffProgressDetails(dispatch)
    }, [dispatch])

    const updateChart = (filteredData: ChartData<'bar'>): void => {
        // Calculate a dynamic height based on the number of projects
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        const projectCount = filteredData.labels?.length ?? 0
        const calculatedHeight = Math.max(300, projectCount * 40)
        setChartHeight(calculatedHeight)

        const options: ChartOptions<'bar'> = {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false, // Important for custom sizing
            scales: {
                x: {
                    stacked: true,
                    ticks: {
                        stepSize: 5,
                        font: {
                            size: 11,
                        },
                    },
                    grid: {
                        display: true,
                        lineWidth: 1,
                        color: 'rgba(0, 0, 0, 0.1)',
                        // @ts-expect-error: dash is not in types but supported by Chart.js
                        dash: [5, 5],
                    },
                    max: 20, // Setting max value to 20 as seen in the screenshot
                },
                y: {
                    stacked: true,
                    ticks: {
                        font: {
                            size: 11,
                        },
                    },
                },
            },
            layout: {
                padding: {
                    top: 10,
                    right: 20,
                    bottom: 10,
                    left: 10,
                },
            },
            plugins: {
                legend: {
                    display: false, // Hide the built-in legend
                },
                tooltip: {
                    enabled: true,
                    mode: 'index',
                    intersect: false,
                },
            },
        }

        if (chartInstance.current != null) {
            chartInstance.current.destroy()
        }

        if (chartRef.current != null) {
            chartInstance.current = new Chart(chartRef.current, {
                type: 'bar',
                data: filteredData,
                options,
            })
        }
    }

    const handleUserChange = (value: number | null): void => {
        const userName =
            userList.find((user) => user?.value === value)?.label ?? ''

        let filteredProjects: ITSTaffprogressDetailsData[]
        // Filter tasks by selected user
        if (value !== undefined && value !== null) {
            filteredProjects = ItStaffProgressList.filter(
                (project) => project.firstName === userName
            )
        } else {
            filteredProjects = ItStaffProgressList
        }

        // Prepare data for the chart
        const data: ChartData<'bar'> = {
            labels: filteredProjects?.map((project) => project?.projectName),
            datasets: [
                {
                    label: 'To Do',
                    data: filteredProjects.map(
                        (project) => project.openTaskCount
                    ),
                    backgroundColor: '#7DA8FF',
                    borderRadius: 0,
                    borderSkipped: false,
                },
                {
                    label: 'In Progress',
                    data: filteredProjects.map(
                        (project) => project.inProgressTaskCount
                    ),
                    backgroundColor: '#B9ADF9',
                    borderRadius: 0,
                    borderSkipped: false,
                },
                {
                    label: 'Done',
                    data: filteredProjects.map(
                        (project) => project.doneTaskCount
                    ),
                    backgroundColor: '#6EE7B7',
                    borderRadius: 0,
                    borderSkipped: false,
                },
            ],
        }

        // Update the chart
        updateChart(data)
    }

    useEffect(() => {
        // Initialize chart with all data
        if (ItStaffProgressList?.length > 0) {
            const data: ChartData<'bar'> = {
                labels: ItStaffProgressList?.map(
                    (project) => project?.projectName
                ),
                datasets: [
                    {
                        label: 'To Do',
                        data: ItStaffProgressList.map(
                            (project) => project.openTaskCount
                        ),
                        backgroundColor: '#7DA8FF',
                        borderRadius: 0,
                        borderSkipped: false,
                    },
                    {
                        label: 'In Progress',
                        data: ItStaffProgressList.map(
                            (project) => project.inProgressTaskCount
                        ),
                        backgroundColor: '#B9ADF9',
                        borderRadius: 0,
                        borderSkipped: false,
                    },
                    {
                        label: 'Done',
                        data: ItStaffProgressList.map(
                            (project) => project.doneTaskCount
                        ),
                        backgroundColor: '#6EE7B7',
                        borderRadius: 0,
                        borderSkipped: false,
                    },
                ],
            }
            updateChart(data)
        }
    }, [ItStaffProgressList])

    // Custom legend rendering
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const renderCustomLegend = () => (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexWrap: 'wrap',
                padding: '8px',
            }}
        >
            {legendItems.map((item, index) => (
                <div
                    key={index}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginRight: '20px',
                    }}
                >
                    <div
                        style={{
                            width: '10px',
                            height: '10px',
                            minWidth: '10px',
                            borderRadius: '50%',
                            backgroundColor: item.color,
                            marginRight: '5px',
                        }}
                    />
                    <span style={{ fontSize: '11px' }}>{item.text}</span>
                </div>
            ))}
        </div>
    )

    return (
        <div className={styles.marginRight10}>
            <Card
                className={`${styles.card} ${styles.client__card__height}`}
                bodyStyle={{ padding: '16px' }}
            >
                <div className={styles.cardContent}>
                    <Row>
                        <Col span={20}>
                            <span className={styles.clients__first__left}>
                                Staff Progress
                            </span>

                            <Select
                                style={{
                                    width: '220px',
                                    marginLeft: '8px',
                                }}
                                allowClear
                                placeholder="Select & search staff"
                                onChange={handleUserChange}
                                showSearch
                                filterOption={(input, option) =>
                                    option?.label
                                        ?.toLowerCase()
                                        .includes(input.toLowerCase()) ?? false
                                }
                                options={[
                                    { label: 'Please select', value: null },
                                    ...userList,
                                ]}
                            />
                        </Col>
                    </Row>
                </div>
                <Row style={{ marginTop: 20, marginBottom: 20 }}></Row>
                <Row>
                    {ItStaffProgressList?.length === 0 ? (
                        <Col
                            span={24}
                            style={{
                                textAlign: 'center',
                                padding: '20px',
                                height: '120px',
                            }}
                        >
                            <Empty description="No data available" />
                        </Col>
                    ) : (
                        <div
                            style={{
                                position: 'relative',
                                width: '100%',
                                height: `${chartContainerHeight + legendHeight}px`,
                            }}
                        >
                            {/* Chart Area - Scrollable */}
                            <div
                                style={{
                                    height: `${chartContainerHeight}px`,
                                    width: '100%',
                                    overflowY: 'auto',
                                    overflowX: 'hidden',
                                }}
                            >
                                <div
                                    style={{
                                        height: `${chartHeight}px`,
                                        width: '100%',
                                    }}
                                >
                                    <canvas ref={chartRef}></canvas>
                                </div>
                            </div>

                            {/* Legend Area - Fixed position at the bottom */}
                            <div
                                style={{
                                    position: 'absolute',
                                    marginTop: 5,
                                    left: 0,

                                    width: '100%',
                                    height: `${legendHeight}px`,
                                    background: 'white', // Match your card background
                                    borderTop: '1px solid #f0f0f0', // Optional divider
                                    zIndex: 10, // Ensure legend stays on top
                                }}
                            >
                                {renderCustomLegend()}
                            </div>
                        </div>
                    )}
                </Row>
            </Card>
        </div>
    )
}

const mapStateToProps = (state: any): ITStaffProgressStateInteface => {
    return {
        userList: state.user.userList,
        ItStaffProgressList: state.itdashboardInfo.ItStaffProgressList,
    }
}

export default connect(mapStateToProps)(TaskProgressChart)
